import {
  Accordion,
  AddIcon,
  AllProductsIcon,
  Box,
  CollapseIcon,
  ColorPaletteIcon,
  Flex,
  FontIcon,
  LogoIcon,
  ProductCardIcon,
  SearchIcon,
  Tag,
  Typography,
} from '@iheartjane/reefer';
import { useFormContext } from '@iheartjane/reefer-hook-form';
import type { GoogleFontFamily } from '@iheartjane/types';
import { clsx } from 'clsx';
import isEmpty from 'lodash-es/isEmpty';
import isMatchWith from 'lodash-es/isMatchWith';
import isNil from 'lodash-es/isNil';
import isObject from 'lodash-es/isObject';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { useFetchCurrentBloomMenu } from '@jane/business-admin/data-access';

import { CategoryTabsSection } from './sections/CategoryTabsSection';
import { ColorsSection } from './sections/ColorsSection';
import { FontsSection } from './sections/FontsSection';
import { LogoSection } from './sections/LogoSection';
import { ProductCardsSection } from './sections/ProductCardsSection';
import { SearchSection } from './sections/SearchSection';

import styles from './EditorSidebar.module.css';

// Section id should match form name so we can keep track of form edits
export type SidebarSection =
  'logo' | 'colors' | 'fonts' | 'product_cards' | 'category_tabs' | 'search';

interface SidebarSectionProps {
  activeSection: SidebarSection | null;
  allowOverflow?: boolean;
  children: ReactNode;
  id: SidebarSection;
  setActiveSection: (section: SidebarSection | null) => void;
  title: string;
}

const SidebarSection = ({
  activeSection,
  allowOverflow,
  children,
  id,
  setActiveSection,
  title,
}: SidebarSectionProps) => {
  const {
    formState: { dirtyFields, errors },
    watch,
  } = useFormContext();

  const { data: bloomMenuData } = useFetchCurrentBloomMenu();
  const defaultTheme = bloomMenuData?.default_theme?.bloom;
  const formValues = watch(id);
  const hasErrors = errors[id];

  const hasDefaultValues = useMemo(() => {
    if (id === 'fonts' && defaultTheme?.fonts) {
      // Fonts can be undefined or null, check for both when comparing
      return isMatchWith(
        formValues,
        defaultTheme.fonts,
        (source, comparison) => {
          if (isNil(source) && isNil(comparison)) {
            return true;
          }
          return false;
        }
      );
    } else if (id === 'colors' && defaultTheme?.colors) {
      // Form values only has main colors so only check those when comparing
      return isMatchWith(
        formValues,
        defaultTheme.colors,
        (source, comparison) => {
          if (source?.main === comparison?.main) {
            return true;
          }
          return false;
        }
      );
    } else if (id === 'logo') {
      return formValues === undefined;
    } else {
      return;
    }
  }, [JSON.stringify(formValues), JSON.stringify(defaultTheme), id]);

  const isActive = activeSection === id;

  const hasEdits = useMemo(() => {
    return isObject(dirtyFields[id])
      ? !isEmpty(dirtyFields[id])
      : dirtyFields[id];
  }, [JSON.stringify(dirtyFields), id]);

  return (
    <Accordion.Item id={id} expanded={isActive}>
      <Box mb={16} background="grays-white" borderRadius="sm">
        <Accordion.Heading
          p={20}
          disableExpansion
          data-testid={`Accordion ${title}`}
          onClick={() =>
            setActiveSection(isActive ? null : (id as SidebarSection))
          }
        >
          <Flex
            gap={8}
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Typography variant="body-bold">{title}</Typography>

            <Flex alignItems="center" gap={12}>
              {['colors', 'fonts', 'logo'].includes(id) && hasDefaultValues && (
                <Tag
                  background="purple-light"
                  color="purple"
                  icon={
                    <AddIcon
                      style={{ width: 12, height: 12 }}
                      mr={2}
                      ml={4}
                      color="purple"
                    />
                  }
                  label="Add"
                />
              )}
              {hasEdits && (
                <Box
                  width={12}
                  height={12}
                  background={hasErrors ? 'error' : 'purple'}
                  borderRadius="lg"
                ></Box>
              )}
              <Accordion.Indicator />
            </Flex>
          </Flex>
        </Accordion.Heading>

        <Accordion.Content px={20} pb={20} allowOverflow={allowOverflow}>
          {children}
        </Accordion.Content>
      </Box>
    </Accordion.Item>
  );
};

type IconSidebarProps = {
  collapsed: boolean;
  collapseSidebar: () => void;
  expandSection: (section: SidebarSection) => void;
};

const IconSidebarButton = ({
  ariaLabel,
  children,
  onClick,
}: {
  ariaLabel: string;
  children: ReactNode;
  onClick: () => void;
}) => {
  return (
    <Flex
      className={styles['ultralightHover']}
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      aria-label={ariaLabel}
      borderRadius="sm"
      height="72px"
      width="82px"
      onClick={onClick}
      type="button"
    >
      {children}
    </Flex>
  );
};

const IconSidebar = ({
  collapsed,
  collapseSidebar,
  expandSection,
}: IconSidebarProps) => {
  return (
    <Flex
      className={clsx([styles['iconSidebar'], styles['grayBorder']], {
        [styles['iconSidebarShow']]: collapsed,
      })}
      background="grays-white"
      flexDirection="column"
      alignItems="center"
      justifyItems="center"
      position="absolute"
      overflow="hidden"
      width="106px"
      py={12}
      gap={8}
      top={0}
      left={0}
      zIndex={1}
    >
      <IconSidebarButton onClick={collapseSidebar} ariaLabel="Expand">
        <CollapseIcon />
        <Typography variant="mini-bold">Expand</Typography>
      </IconSidebarButton>

      <Box height="1px" background="grays-light" width="80%" />

      <IconSidebarButton onClick={() => expandSection('logo')} ariaLabel="Logo">
        <LogoIcon />
        <Typography variant="mini-bold">Logo</Typography>
      </IconSidebarButton>

      <IconSidebarButton
        onClick={() => expandSection('colors')}
        ariaLabel="Colors"
      >
        <ColorPaletteIcon />
        <Typography variant="mini-bold">Colors</Typography>
      </IconSidebarButton>

      <IconSidebarButton
        onClick={() => expandSection('fonts')}
        ariaLabel="Font"
      >
        <FontIcon />
        <Typography variant="mini-bold">Font</Typography>
      </IconSidebarButton>

      <IconSidebarButton
        onClick={() => expandSection('product_cards')}
        ariaLabel="Product card"
      >
        <ProductCardIcon />
        <Typography variant="mini-bold">Product card</Typography>
      </IconSidebarButton>

      <IconSidebarButton
        onClick={() => expandSection('category_tabs')}
        ariaLabel="Categories"
      >
        <AllProductsIcon color="grays-black" />
        <Typography variant="mini-bold">Categories</Typography>
      </IconSidebarButton>

      <IconSidebarButton
        onClick={() => expandSection('search')}
        ariaLabel="Search + AI"
      >
        <SearchIcon />
        <Typography variant="mini-bold">Search + AI</Typography>
      </IconSidebarButton>
    </Flex>
  );
};

export const EditorSidebar = ({
  activeSection,
  collapsed,
  collapseSidebar,
  expandSidebarWithSection,
  isStackedView,
  isTabletMobileView,
  setActiveSection,
  supportedFontFamilies,
}: {
  activeSection: SidebarSection | null;
  collapsed: boolean;
  collapseSidebar: () => void;
  expandSidebarWithSection: (section: SidebarSection) => void;
  isStackedView: boolean;
  isTabletMobileView: boolean;
  setActiveSection: (section: SidebarSection | null) => void;
  supportedFontFamilies: GoogleFontFamily[];
}) => {
  const sectionProps = { activeSection, setActiveSection };

  return (
    <Flex
      flexDirection="column"
      gap={24}
      className={clsx(styles['sidebarWrapper'], {
        [styles['stackedSidebar']]: isStackedView,
        [styles['compactSidebar']]: isTabletMobileView,
        [styles['collapsedSidebar']]: collapsed,
      })}
      shrink={0}
    >
      <IconSidebar
        collapsed={collapsed}
        collapseSidebar={collapseSidebar}
        expandSection={expandSidebarWithSection}
      />

      <Flex
        className={styles['sidebarHeader']}
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="section-bold" as="h2">
          Menu
        </Typography>

        <Flex
          className={clsx(styles['grayBorder'], styles['ultralightHover'])}
          type="button"
          aria-label="Collapse"
          onClick={collapseSidebar}
          py={8}
          px={12}
        >
          <CollapseIcon mr={8} />
          <Typography>Collapse</Typography>
        </Flex>
      </Flex>

      <Accordion className={styles['accordion']}>
        <SidebarSection id="logo" title="Logo" {...sectionProps}>
          <LogoSection />
        </SidebarSection>

        <SidebarSection id="colors" title="Colors" {...sectionProps}>
          <ColorsSection />
        </SidebarSection>

        <SidebarSection
          id="fonts"
          title="Fonts"
          allowOverflow
          {...sectionProps}
        >
          <FontsSection supportedFontFamilies={supportedFontFamilies} />
        </SidebarSection>

        <SidebarSection
          id="product_cards"
          title="Product cards"
          {...sectionProps}
        >
          <ProductCardsSection />
        </SidebarSection>

        <SidebarSection id="category_tabs" title="Categories" {...sectionProps}>
          <CategoryTabsSection />
        </SidebarSection>

        <SidebarSection
          id="search"
          title="Search and AI Budtender"
          {...sectionProps}
        >
          <SearchSection />
        </SidebarSection>
      </Accordion>
    </Flex>
  );
};
